// StatusPill — a compact, inline-flex pill for status badges (cases,
// orgs, members). Replaces a dozen ad-hoc pill shapes across the app
// that all had the same bug: rendered as block, they stretched to the
// column width instead of hugging their label.
//
// Tone palette matches the design system traffic lights:
//   - neutral: muted surface, foreground text
//   - positive: traffic-green/15 surface, traffic-green-text text
//   - attention: traffic-amber/15 surface, traffic-amber-text text
//   - danger: traffic-red/15 surface, traffic-red text
//   - info: primary-soft surface, primary text
//
// A11y: the base `traffic-green`/`traffic-amber` tokens are tuned for
// fills/dots, not text (fails WCAG 2.2 AA as foreground text on their own
// tint). Pill/badge *text* uses the darker `-text` variants; dots keep the
// full-strength base token since dots are decorative (aria-hidden).
//
// All pills are inline-flex by default and respect the row's vertical
// alignment so they sit next to surrounding copy without forcing a
// line break.

import type { ReactNode } from 'react';

export type Tone = 'neutral' | 'positive' | 'attention' | 'danger' | 'info';

const TONE_CLASSES: Record<Tone, string> = {
  neutral: 'bg-muted text-muted-foreground',
  positive: 'bg-traffic-green/15 text-traffic-green-text',
  attention: 'bg-traffic-amber/15 text-traffic-amber-text',
  danger: 'bg-traffic-red/15 text-traffic-red',
  info: 'bg-primary-soft text-primary',
};

// Solid dot fills for the same tones. Used by StatusDot and the optional
// `dot` on StatusPill. Traffic tones use the full-strength token; neutral
// falls back to a muted grey (matches the design's inactive dots).
const DOT_CLASSES: Record<Tone, string> = {
  neutral: 'bg-muted-foreground/40',
  positive: 'bg-traffic-green',
  attention: 'bg-traffic-amber',
  danger: 'bg-traffic-red',
  info: 'bg-primary',
};

// A bare status dot (User Journeys v2 — the coloured dots on the Manager
// team-check-in list). Decorative: the adjacent label carries the meaning,
// so the dot is `aria-hidden` and colour is never the sole signal.
export function StatusDot({ tone = 'neutral' }: { tone?: Tone }) {
  return (
    <span
      aria-hidden
      className={['inline-block size-2 shrink-0 rounded-full', DOT_CLASSES[tone]].join(' ')}
    />
  );
}

interface StatusPillProps {
  tone?: Tone;
  children: ReactNode;
  title?: string;
  /** Prepend a solid tone dot inside the pill (colour-independent label + colour). */
  dot?: boolean;
}

export function StatusPill({ tone = 'neutral', children, title, dot = false }: StatusPillProps) {
  return (
    <span
      // inline-flex + w-fit keeps the pill hugging its label rather
      // than stretching across a table cell. `whitespace-nowrap` keeps
      // multi-word labels from breaking mid-phrase.
      className={[
        'inline-flex w-fit items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap',
        TONE_CLASSES[tone],
      ].join(' ')}
      title={title}
    >
      {dot ? <StatusDot tone={tone} /> : null}
      {children}
    </span>
  );
}

// Humanised mappings used across the app. Keep these centralised so
// a tone tweak in one place cascades everywhere — previously each
// page had its own `formatStatus` helper that drifted independently.
export const STATUS_TONE: Record<string, Tone> = {
  // case status
  Open: 'info',
  Closed: 'neutral',
  // org status
  Pending: 'attention',
  Active: 'positive',
  Suspended: 'danger',
  // cgc member
  Placeholder: 'neutral',
  // touch point status
  Completed: 'positive',
  // ingestion / audit
  Ready: 'positive',
  Processing: 'attention',
  Failed: 'danger',
  Skipped: 'neutral',
  Unavailable: 'attention',
  // rtw plan
  Acknowledged: 'positive',
};

export function ToneFor(status: string): Tone {
  return STATUS_TONE[status] ?? 'neutral';
}

// Human-readable labels for status enums that read as raw enum values
// in the DB / API. `Other_Family` is the worst offender; without this
// helper it ships to operators with the underscore.
export function humaniseStatus(status: string): string {
  return status.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
}
